import * as React from 'react'

import CloseIcon from '@mui/icons-material/Close'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'

import type { DraftLead } from '@user/store/draftLeads/typings'

import { UpdateRequiredFieldsContent } from './UpdateRequiredFieldsContent'

interface Props {
  lead: DraftLead
  open: boolean
  onClose: () => void
}

/**
 * "Update Required Fields" modal for a Pending Lead flagged as missing auto-review fields. Shows
 * only the missing fields; Save & Auto-Review converts the pending lead and opens the new lead.
 * Mounted only while open so the form resets on close.
 */
export function UpdateRequiredFieldsDialog(props: Props) {
  const { lead, open, onClose } = props

  if (!open) return null

  return (
    <Dialog
      open
      onClose={onClose}
      PaperProps={{ sx: { width: 530 } }}
      data-test-id="update-required-fields-dialog"
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e2e5ea',
          mb: '20px',
        }}
      >
        <Typography variant={'subtitle3'}>Update Required Fields</Typography>

        <IconButton
          onClick={onClose}
          size={'small'}
          sx={{
            cursor: 'pointer',
          }}
          data-test-id="update-required-fields-close"
        >
          <CloseIcon fontSize={'small'} />
        </IconButton>
      </DialogTitle>

      <Typography variant="body2" px={'24px'}>
        This pending lead is missing required fields needed for auto-review.
        Complete them below to convert it to a Lead.
      </Typography>

      <UpdateRequiredFieldsContent lead={lead} onClose={onClose} />
    </Dialog>
  )
}

UpdateRequiredFieldsDialog.displayName = 'UpdateRequiredFieldsDialog'
